import { FormikProps } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { KeyedMutator } from 'swr';

import { LosType } from '@modules/ContactManager/components/constants';
import { IAssetLienHolderResponse, IContactHeaderCreditScore, ILeadData } from '@modules/ContactManager/components/Header/interfaces';

import { IDealCalculatorRequest } from '../../shared/DealCalculator/interfaces';
import { NContact } from '../Contact/validation';
import { RelationshipEnum } from './components/CoBorrower/types';
import { ProductPlan } from './components/Products/interfaces';

export enum EDealType {
  Refinance = 'Refinance',
  Finance = 'Finance',
}

export enum ECo {
  No = 1,
  Yes = 2,
  Add = 3,
  Drop = 4,
  AddAndDrop = 5,
}

export interface ILenderResponse {
  lenderId: string;
  lenderName: string;
  lenderCode: string;
  daysToFirstPayment: number;
  useAprBasedCalculation: boolean;
  documentSubmitType: number;
  isDealerTrack: boolean;
  isQuilo: boolean;
  generalInfo: IGeneralInfo;
  payeeName: string;
  payeeAddress: string;
  isOpenLending: boolean;
}

export interface IFinanceConditionItem {
  term: number;
  rate: number;
  apr: number;
  salesPrice: number;
  dealTotal: number;
  docFeeTotal: number;
  totalAmountFinanced: number;
  monthlyPaymentAmount: number;
  contractDate: string;
  daysToFirstPayment: number;
  firstPaymentDate: string;
  lastPaymentAmount: number;
  lastPaymentDate: string;
  totalTaxAmount: number;
  totalSalesTaxAmount: number;
  totalProductAmount: number;
  totalProductTaxPercentage: number;
  totalProductTaxAmount: number;
  cashDownAmount: number;
  cashBackAmount: number;
  totalSalesTaxPercentage: number;
  estimatedGrossTotalAmount: number;
  dealFeeItems: IDealFee[];
  rateMarkUpPercentage: number;
  interest: number;
  subTotal: number;
  interestPlusDocFee: number;
  subTotalLessDocFee: number;
  additionalFeesTotal: number;
  frontEndTotal: number;
  backEndTotal: number;
  feesTotal: number;
  sorRequired: boolean;
  pti: number;
  newDti: number;
}

export interface IContact {
  id: string;
  numericId?: number;
  relationShip?: number;
  contactName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  cellPhone?: string;
}

export interface IValuation {
  percentage: number;
  kbbRetail: number;
  kbbWholeSale: number;
  kbbFinanceExpress: number;
  nadaRetail: number;
  nadaTradeIn: number;
}

export interface IDealSummary {
  assetId: string;
  dealId: string;
  leadId: string;
  dealNumericId: number;
  isPicked: boolean;
  currentWorkflowStatus: string;
  lenderName: string;
  lenderCode: string;
  assetTag: string;
  rate: number;
  term: number;
  monthlyPayment: number;
  co: ECo;
  contractDate: string;
}

export enum DealUserTypeEnum {
  Lc = 1,
  Lp = 2,
  Bank = 3,
  Account = 4,
  Title = 5,
  SDR = 6,
  Funder = 7,
  LCA = 8,
}

export enum ContactUserTypeEnum {
  Lc = 1,
  Lp = 2,
  Bank = 3,
  Account = 4,
  Title = 5,
  SDR = 6,
  Funder = 7,
  LCA = 8,
}

export const DealUserTypeLabel = {
  [DealUserTypeEnum.Lc]: 'LC',
  [DealUserTypeEnum.Lp]: 'LP',
  [DealUserTypeEnum.LCA]: 'LCA',
  [DealUserTypeEnum.Bank]: 'Bank',
  [DealUserTypeEnum.Account]: 'Accounting',
  [DealUserTypeEnum.Title]: 'Titles',
  [DealUserTypeEnum.SDR]: 'SDR',
  [DealUserTypeEnum.Funder]: 'Funder',
};

export const ContactUserTypeLabel = {
  [ContactUserTypeEnum.Lc]: 'LC',
  [ContactUserTypeEnum.Lp]: 'LP',
  [ContactUserTypeEnum.LCA]: 'LCA',
  [ContactUserTypeEnum.Bank]: 'Bank',
  [ContactUserTypeEnum.Account]: 'Accounting',
  [ContactUserTypeEnum.Title]: 'Titles',
  [ContactUserTypeEnum.SDR]: 'SDR',
  [ContactUserTypeEnum.Funder]: 'Funder',
};

export const orderedDealDropdown = [
  DealUserTypeEnum.SDR,
  DealUserTypeEnum.Lc,
  DealUserTypeEnum.LCA,
  DealUserTypeEnum.Bank,
  DealUserTypeEnum.Lp,
  DealUserTypeEnum.Account,
  DealUserTypeEnum.Title,
];

export const orderedContactDropdown = [
  ContactUserTypeEnum.SDR,
  ContactUserTypeEnum.Lc,
];

export interface IDealUser {
  dealUserType: DealUserTypeEnum;
  userId: string;
  userName: string;
}

export interface IPayoffInformation {
  payoffSubmissionId: string;
  payoffSubmissionDate: Date;
  refundAmount: number;
  payoffStatus: string;
  trackingNumber: string;
  titleType: string;
}

export interface IDeal {
  id: string;
  assetId: string;
  assetNumericId?: number;
  leadId: string;
  numericId: number;
  dealType: string;
  lenderResponse: ILenderResponse;
  isPicked: boolean;
  isDocsGenerated: boolean;
  isHotTitles: boolean;
  isBuyBack: boolean;
  downPaymentStatus?: string;
  isBlueAdvantage: boolean;
  isGenysisWithHoldings: boolean;
  shipmentBatchId?: string;
  fundedDealShipmentBatchId?: string;
  financeConditionItem: IFinanceConditionItem;
  financeConditionItemResponse?: IFinanceConditionItem;
  dealLos?: IDealLos;
  currentWorkflowStatus: string;
  carrierTrackingNumberIn: string;
  carrierTrackingNumberOut: string;
  cudlAccountNumber?: string;
  assetTag?: string;
  co: number;
  contact: IContact;
  coContact?: IContact;
  droppedCoContact?: IContact;
  dealUsersResponses?: IDealUser[];
  isAch: boolean;
  isGrossUpIncome: boolean;
  postLoanDti: number;
  loanApplicationNumber?: string;
  loanUrl?: string;
  valuations: IValuation[];
  loanToValues?: ILoanToValue[];
  additionalFeeArray?: IDealFee[]; // used on FE only for processing
  dealFeeArray?: IDealFee[]; // used on FE only for processing
  lenderCode?: string;
  dealId?: string;
  payoffInformation?: IPayoffInformation;
  isVoidEnable: boolean;
  allowDroppedDocGenAndDocuSign: boolean;
  currentSteps: ICurrentSteps[];
  disallowWorkflowSteps: string;
  isRecalculatingTitleAndDocFees?: boolean;
  dealAssetResponse?: IAssetLienHolderResponse;
}

export interface IDealLos {
  losType: LosType;
  lastPushDate: string;
  accountNumber: string;
  externalReferenceNumber: string;
  lenderApprovalDate: string;
  lenderApprovalAmount: string;
  lenderCreditScore: string;
  lenderCreditType: string;
  applicationId: string;
  loanApplicationNumber: string;
  postLoanDti: number;
  status: string;
}

export interface ILenderSelctorProps extends Partial<FormikProps<IDeal>> {
  dealResponse?: IDeal;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ILoanToValue {
  ltvType: string;
  valuation: string;
  ltvValue: string;
}

export interface IProduct {
  status: string;
  productTypeName: string;
  providerName: string;
  cost: number;
  markup: number;
  retail: number;
  plan: string;
  details: string;
  dealProductId: string;
}

export interface IEditRetail {
  dealId: string;
  cost: number;
  markup: number;
  retail: number;
  reason: string;
  dealProductId: string;
  dealCalculatorLeadProductId?: string;
  productRules?: ProductPlan[];
  productTypeName?: string;
}

export interface IEditProduct {
  dealProductId: string;
  cost: number;
  markup: number;
  retail: number;
  reason: string;
}

export interface IDealsTableProps {
  dealId: string;
  selectDealId: (dealId: string) => void;
  data?: IDealSummary[];
  contactAddress?: NContact.Address;
  expandable?: boolean;
  deleteDeal?: (deal: string) => void;
  onSelect?(row: IDealSummary): void;
  onCalculate(deal: IDealCalculatorRequest): void;
  handleDealToggle(dealId: string): void;
  error?: Error;
  setShowDealBuilder?: Dispatch<SetStateAction<boolean>>;
  validatingDeals?: boolean;
}

export interface IConditionsProp extends Partial<FormikProps<IDeal>> {
  deal?: IDeal;
  reloadDeal: KeyedMutator<IDeal>;
  otherIncomes? : OtherIncome[] | undefined;
  isLoading: boolean;
  reloadDeals: KeyedMutator<IDealSummary[]>;
  dealSavePreValidation: () => boolean;
}

export interface IDealCalculate {
  [key: string | number | symbol]: string | number | object | undefined | boolean;
}

export interface IDealFee {
  id?: string;
  category: string;
  description: string;
  amount: number;
  isDocFee: boolean;
  isAdditionalFee: boolean;
}

export interface DealFeesModalProps {
  dealId: string;
  type: string;
  handleClose: () => void;
  reloadDeal: KeyedMutator<IDeal>;
  dealFeesArr?: IDealFee[];
  additionalFeeArr?: IDealFee[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  isLoading: boolean;
  dealFeesModalPosition: number;
  dealSavePreValidation: () => boolean;
}

export interface CoApplicant {
  id: string;
  numericId: number;
  firstName: string;
  lastName: string;
  cellPhone: string;
  email: string;
  driveLicenseNumber: string;
  driverLicenseState: string;
  relationshipType: number;
  isDroppedContact: boolean;
}

export interface Loan {
  loanAmount: number;
  term: number;
  rate: number;
  monthlyPayment: number;
  perDiem: number;
  nextPaymentDate: Date;
}

export interface Zip {
  code: string;
}

export interface Address {
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: number;
  zip: Zip;
  type: number;
  county: string;
}

export interface CurrentProduct {
  refundAmount: number;
  productType: number;
}

export interface AssetLienHolder {
  name: string;
  lienHolderId: string;
  remainingLoanAmount: number;
  remainingTerm: number;
  goodDate: Date;
  loan: Loan;
  address: Address;
  currentProducts: CurrentProduct[];
  accountNumber: string;
  phoneNumber: string;
  showBanner: boolean;
}

export interface AssetOption {
  description: string;
  valuationRetailAmount: number;
  valuationWholesaleAmount: number;
  assetValuationProviderOptionId: string;
  isSelected: boolean;
}

export interface BookOut {
  createdDate: Date;
}

export interface AssetValuation {
  type: number;
  providerVehicleId: number;
  baseRetailAmount: number;
  baseTradeInAmount: number;
  baseWholesaleAmount: number;
  baseFinanceExpressAmount: number;
  valuationMileage: string;
  mileageAdjustedRetailAmount: number;
  mileageAdjustedTradeInAmount: number;
  mileageAdjustedWholesaleAmount: number;
  valuationRegion: string;
  valuationZip: string;
  valuationDate: Date;
  assetOptions: AssetOption[];
  optionAdjustedRetailAmount: number;
  optionAdjustedTradeInAmount: number;
  optionAdjustedWholesaleAmount: number;
  bookOut: BookOut;
}

export interface AssetRegistration {
  primaryOwner: string;
  secondaryOwner: string;
  number: string;
  state: string;
  expirationDate: Date;
  registeredOwners: number;
  isPrimaryChange: boolean;
  isSecondaryChange: boolean;
  nameChangeOwnerName2Flag: boolean;
  nameChangeOwnerName1Flag: boolean;
  titleNumber: string;
}

export interface AssetInsurance {
  company: string;
  phoneNumber: string;
  policyNumber: string;
  effectiveDate: Date;
  expirationDate: Date;
}

export interface CreatedBy {
  id: string;
  email: string;
  name: string;
}

export interface UpdatedBy {
  id: string;
  email: string;
  name: string;
}

export interface ContactAsset {
  id: string;
  assetType: number;
  vin: string;
  year: number;
  make: string;
  model: string;
  kbbTrim: string;
  nadaTrim: string;
  bodyStyle: string;
  mileage: string;
  assetCondition: number;
  color: string;
  assetUsage: string;
  msrp: number;
  assetLienHolder: AssetLienHolder;
  assetValuations: AssetValuation[];
  assetRegistration: AssetRegistration;
  assetInsurance: AssetInsurance;
  createdBy: CreatedBy;
  createdDate: Date;
  updatedBy: UpdatedBy;
  updatedDate: Date;
  isDeleted: boolean;
  contactId: string;
  ucgVehicleId: string;
  vId: string;
  numericId: number;
}

export interface Notification {
  message: string;
  key: string;
  data: string;
  color: number;
}

export interface Fico {
  bureauType: number;
  fico: string;
}

export interface Address2 {
  isCurrent: boolean;
  includeInDti: boolean;
  monthsAtAddress: number;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  county: string;
  unit: string;
  monthlyPayment: number;
  cdfitm?: boolean;
}

export interface Employment {
  monthsAtJob: number;
  jobTitle: string;
  employmentType: number;
  companyName: string;
  monthlyIncomeAmount: number;
  isCurrent: boolean;
  isPrimary: boolean;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
  addressType: string;
  county: string;
  unit: string;
  phone: string;
  reportedToPhoneNumber: string;
  startDate: string;
}

export interface OtherIncome {
  monthlyAmount: number;
  incomeType: string;
  isCurrent: boolean;
  monthsAtJob: number;
}

export interface Reference {
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  county: string;
  relationshipType: number;
}

export interface IContactData {
  coApplicants: CoApplicant[];
  asset: ContactAsset;
  notifications: Notification[];
  fico: Fico[];
  id: string;
  numericId: number;
  firstName: string;
  middleInitial: string;
  lastName: string;
  suffix: string;
  maidenName: string;
  ssn: string;
  last4OfSsn: string;
  dateOfBirth: string;
  homePhone: string;
  workPhone: string;
  workPhoneExtension: string;
  cellPhone: string;
  email: string;
  workEmail: string;
  militaryStatus: number;
  maritalStatus: number;
  driverLicenseNumber: string;
  driverLicenseExpireDate: string;
  driverLicenseIssuesOnDate: string;
  driverLicenseState: string;
  isMilitaryLendingActAvailable: boolean;
  hasProhibitedTradeLine: boolean;
  residencyStatusType: number;
  isOCRReviewRequired: boolean;
  isDeceased: boolean;
  isOptOut: boolean;
  oneCreditPull: boolean;
  referralContactName: string;
  referralContactId: string;
  referralEmail: string;
  createdDate: string;
  addresses: Address2[];
  employments: Employment[];
  otherIncomes: OtherIncome[];
  references: Reference[];
  addressSummary: string;
  employmentSummary: string;
  incomeSummary: string;
  expenseAmount: number;
}

type ContactType = {
  contactId: string;
  relationShipType: RelationshipEnum;
};

export interface IContactsRequestBody {
  coContact: ContactType | null;
  droppedCoContact: ContactType | null;
}
export interface IPushAppData {
  dealId?: string;
  overrideHughesData?: boolean;
  overrideData?: boolean;
  comment?: string;
  originalLoanAmount?: number | null;
  openDate?: string;
}

export interface IPushAppDTData {
  dealId?: string;
  comment?: string;
}

export interface IPushSubprimeLenderData {
  dealId: string;
  dealNumericId: number;
  leadId: string;
  lenderCode: string;
  lenderName: string;
  overrideHughesData: boolean;
  overrideData: boolean;
  originalLoanAmount: number;
  comment: string;
}

export interface ISubprimeResponse {
  success?: boolean;
  isSuccess?: boolean;
  message: string;
  dealId: string;
  leadId: string;
  lenderId: number;
  lenderName: string;
  dealNumericId: number;
}

export interface ILenderComment {
  lenderComment: string;
}

export interface FinancingDetailsModalProps {
  dealId: string;
  deal?: IDeal;
  onClose(): void;
  financingModalPosition?: number;
  assetId: string;
}

export interface ListItemField {
  name: string;
  values: string[];
}

export interface ListDecision {
  status: string;
  statusDate: string;
}

export interface IApprovedTerms {
  rate: number;
  term: number;
  vehicleWholesaleValue: number;
  loanAmount: number;
  gap: number;
  serviceContracts: number;
  creditLife: number;
  creditDisability: number;
  totalBackends: number;
  totalLoanAmount: number;
  monthlyPayment: number;
  backendAllowance: number;
}

export interface IRequestedTerms {
  rate: number;
  term: number;
  vehicleWholesaleValue: number;
  loanAmount: number;
  gap: number;
  serviceContracts: number;
  creditLife: number;
  creditDisability: number;
  totalBackends: number;
  totalLoanAmount: number;
  monthlyPayment: number;
  backendAllowance: number;
}

export interface IUnderwriting {
  applicationId: string;
  pricingScore: number;
  creditDepth: number;
  origination: number;
  tradeFrontendLtv: number;
  tradeFinalLtv: number;
  retailFrontendLtv: number;
  retailFinalLtv: number;
  actualDti: number;
  actualPti: number;
  maximumDti: number;
  maximumPti: number;
  tradeInPayment: number;
  ltv: number;
  dti: number;
  pti: number;
}

export interface ICallbackInformation {
  term: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  informationValues: any;
}

export interface IFinanceDetails {
  decisionStatus: string;
  declineReasons: string[];
  loanTerms: {
    approvedTerms: IApprovedTerms;
    requestedTerms: IRequestedTerms;
  };
  underwriting: IUnderwriting;
  callbackInformation: ICallbackInformation[];
  termsAndStipulation: {
    requiredDocumentation: string[];
    termsAndConditions: string[];
  };
  applicationDisplayItems: ListItemField[];
  decisionDisplayItems: ListItemField[];
  contentApplication: string;
  contentDecision: string;
  lenderResponseDisplayType?: number;
  decisionStatuses?: any;
}

export interface IDelaysModalProps {
  openDelayModalPosition: number;
  handleClose: () => void;
}

export interface IDelaysFormProps {
  delayData: IDelays;
}

export interface IFundDelay {
  isDelay: boolean;
  userName: string;
  userId: string;
}

export interface IDelayFile {
  isDelayFile: boolean;
  userName: string;
  userId: string;
}

export interface ICriticalFundDelay {
  isCriticalDelay: boolean;
  userName: string;
  userId: string;
}

export interface IDelays {
  dealId: string;
  fundDelay: IFundDelay[];
  delayFile: IDelayFile[];
  criticalFundDelay: ICriticalFundDelay[];
  selectedFundDelay: string[];
  selectedDelayFile: string[];
  selectedCriticalFundDelay: string[];
}

export interface IItems {
  documentName: string;
  isReceived: boolean;
}

export interface IComments {
  comment: string;
  createdById: string;
  createdDate: string;
}

export interface IComment {
  comment: string;
  createdById: string;
  createdDate: string;
}

export interface ITitleDocuments {
  dealId: string;
  items: IItems[];
  comments: IComments[];
  selectedItems: string[];
  comment: IComment;
}

export interface IDocumentsDmvPackate {
  assetState: string;
  dealBorrowerType: number;
  documentNames: string[];
}

export interface ITitleDocumentsFormProps {
  values: ITitleDocuments;
}

export interface IDocsTitleMenuProps {
  handleClose: () => void;
  assetId: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

export interface IGeneralInfo {
  maxVrp: number;
  maxVsc: number;
  achDiscount: number;
  flatPercent: number;
  requirements: string;
  defaultValuationMethods?: string[];
}

export interface ILenderInfo {
  id: string;
  name: string;
  address: string;
  state: string;
  generalInfo: IGeneralInfo;
}

export interface DealsProps {
  deals: IDealSummary[];
  otherIncomes? : OtherIncome[] | undefined;
  selectLeadId: (leadId: string) => void;
  creditScores?: IContactHeaderCreditScore[];
  userCurrentAddress?: NContact.Address;
  reloadLeadData: KeyedMutator<ILeadData>;
  reloadDeals: KeyedMutator<IDealSummary[]>;
  validatingDeals: boolean;
  dealsSummaryError?: Error;
  reloadContactAlerts: () => void;
}

export interface IDealBuilderProps {
  data?: IDeal;
  deals?: IDealSummary[];
  setFieldValue: <T>(field: string, value: T, shouldValidate?: boolean) => void;
  setFieldError: (field: string, errorMsg: string) => void;
  refreshDealsTable?: () => Promise<void>;
  creditScores?: IContactHeaderCreditScore[];
}

export enum CheckRequestType {
  CheckRequest = '1',
  ProductCancelCheck = '2',
}

export enum SubprimeType {
  createDeals = 'createDeals',
}

export interface IChangeEventProps {
  target: {
    name: string;
    value: number;
    source: string;
  };
}

export interface IPayoffStatus {
  status: string;
  responseMessage: string;
  statusComment: string;
  titleType: string;
  trackingNo: string;
}
export interface IDealSheetAdjustment {
  dealId: string;
  contactId: string;
  amountFinanced: number;
  adjPayOff: number;
  totalAdjustment: number;
}

export interface IProductsFetch {
  dealsProducts: IProduct[];
}

export interface IOpenLendingMisMatch {
  html: string;
}

export interface IDealComment {
  dealId: string;
  comment: string;
  commentActionType: number;
}

export interface ICurrentSteps {
  trackerStageId: number;
  workflowNumericId: number;
  workflowStepNumericId: number;
}
